import React from 'react'
import { createPortal } from 'react-dom'
import { MdOutlineKeyboardBackspace } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { LanguageDropdown } from '../LanguageDropdown'
import ThemeToggle from '../ThemeToggle/ThemeToggle'
import { useTheme } from '../../context/ThemeProvider'
import { useThemeAssets } from '../../hooks/useThemeAssets'
import { SidePanelProps } from './SidePanel.types'
import { LANGUAGES } from '../../i18n/constants'
import { getThemeMode } from '../../utils/theme-utils'
import { ThemeMode } from '../../constants/theme'

const defaultLanguages = LANGUAGES

const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  onClose,
  username,
  currentLang,
  onLanguageChange,
  languages = defaultLanguages,
  logo,
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { svgAssets } = useThemeAssets()

  // Get theme-based logo (using dynamic assets from useThemeAssets)
  const isDarkMode = getThemeMode(theme) === ThemeMode.DARK
  const defaultLogo = isDarkMode ? svgAssets.LOGO.dark : svgAssets.LOGO.light

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const sidePanel = (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Overlay with blur */}
      <div
        className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300 ease-in-out ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleOverlayClick}
      />

      {/* Side Panel */}
      <div
        className={`relative w-80 h-full bg-[var(--navbar-bg)] shadow-2xl transform transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b border-[var(--navbar-border)]">
          <button
            title={t('common.close')}
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[var(--card-disabled-bg)] transition-colors"
          >
            <MdOutlineKeyboardBackspace className="text-lg text-[var(--user-id-text)]" />
          </button>

          {/* User Avatar */}
          <div className="flex items-center justify-center w-12 h-12 rounded-full active-gradient">
            <span className="text-xl font-bold text-[var(--balance-text-color)]">
              {username.charAt(0).toUpperCase()}
            </span>
          </div>

          {/* User Info */}
          <div>
            <h3 className="text-lg font-semibold text-[var(--user-id-text)]">
              {username}
            </h3>
          </div>
        </div>

        {/* Settings Section */}
        <div className="p-6 space-y-6">
          {/* Language Section */}
          <div>
            <h4 className="text-sm font-medium text-[var(--card-subtext)] mb-3">
              {t('sidePanel.language')}
            </h4>
            <LanguageDropdown
              currentLang={currentLang}
              onLanguageChange={onLanguageChange}
              languages={languages}
            />
          </div>

          {/* Theme Mode Section */}
          <div>
            <h4 className="text-sm font-medium text-[var(--card-subtext)] mb-3">
              {t(!isDarkMode ? 'sidePanel.lightMode' : 'sidePanel.darkMode')}
            </h4>
            <ThemeToggle />
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-[var(--navbar-border)]">
          <div className="flex items-center gap-3">
            {/* Logo */}
            <img
              src={logo || defaultLogo}
              alt="RB7 Technology"
              className="h-16 w-20 object-contain"
            />

            {/* Company Info */}
            {/* <div style={{ display: 'hidden' }}>
              <h5 className="text-sm font-semibold text-[var(--user-id-text)]">
                {t('sidePanel.companyName')}
              </h5>
              <p className="text-xs text-[var(--card-subtext)]">
                {t('sidePanel.allRightsReserved')}
              </p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(sidePanel, document.body)
}

export default SidePanel
